// İçe aktarma Adım 2 × kırılım (spec §7.3) — türetimler: öneri tablosu satırları, üst gösterge
// seçenekleri, toplam satırı rozetleri ve "grup sette var mı" sorgusu. Saf kurallar
// lib/kpi-import-breakdown'da; burada yalnız memo (ImportMappingStep 600 satır sınırı).

import { useMemo } from "react";
import type { KpiImportColumn, KpiImportRow, KpiMappingRow, KpiSetIndicator } from "@mahir/shared";
import {
  breakdownParentOptions,
  breakdownSuggestions,
  totalRowLines,
  type BreakdownSuggestion,
} from "../lib/kpi-import-breakdown";
import type { SearchSelectOption } from "../lib/search-select";

export interface ImportBreakdownView {
  suggestions: BreakdownSuggestion[];
  parentOptions: SearchSelectOption[];
  totalLines: Map<string, { matched: boolean; text: string }>;
  groupInSet: (parentCode: string, dimension: string) => boolean;
}

export function useImportBreakdown(args: {
  enabled: boolean;
  mapping: readonly KpiMappingRow[];
  gridRows: ReadonlyMap<string, KpiImportRow>;
  columns: readonly (readonly KpiImportColumn[])[];
  sheetOfRow: ReadonlyMap<string, number>;
  labelByCode: ReadonlyMap<string, string>;
  indicators: readonly KpiSetIndicator[];
}): ImportBreakdownView {
  const { enabled, mapping, gridRows, columns, sheetOfRow, labelByCode, indicators } = args;
  const suggestions = useMemo(
    () =>
      enabled
        ? breakdownSuggestions(mapping, {
            gridRows,
            columnsOfRow: (rowId) => columns[sheetOfRow.get(rowId) ?? 0] ?? [],
            sheetOfRow,
            labelByCode,
            indicators,
          })
        : [],
    [enabled, mapping, gridRows, columns, sheetOfRow, labelByCode, indicators],
  );
  const parentOptions = useMemo(() => breakdownParentOptions(indicators), [indicators]);
  const totalLines = useMemo(() => totalRowLines(suggestions), [suggestions]);
  const groupInSet = useMemo(() => {
    const byCode = new Map(indicators.map((i) => [i.code, i]));
    return (parentCode: string, dimension: string): boolean => {
      const parentId = byCode.get(parentCode)?.id;
      if (parentId === undefined) return false;
      return indicators.some(
        (i) =>
          i.parentId === parentId &&
          i.nodeKind === "breakdown_group" &&
          i.breakdown?.dimension === dimension,
      );
    };
  }, [indicators]);
  return { suggestions, parentOptions, totalLines, groupInSet };
}
